import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import {
  SidePanelConfig,
  WebpageRecord,
  WidgetRecord,
} from "../../../../../interfaces/Interfaces";
import { getTranslations } from "../../../../../translations/Translations";
import { Form, Formik } from "formik";
import {
  MyColorPicker,
  MyNumberTextField,
  MyTextAreaField,
  MyTextField,
} from "./Fields";
import { useRevalidator } from "react-router-dom";
import SidePanel from "./SidePanel";
import {
  returnDefaultJsonConfig,
  returnDefaultWebpageJsonConfig,
  webpageOptions,
  widgettypes,
} from "./WidgetField";
import TypeOptions from "./TypeOptions";

const translations = getTranslations();

const findDifferences = (obj1: any, obj2: any) => {
  const differences: any = {};

  Object.keys(obj1).forEach((key) => {
    if (obj1[key] !== obj2[key]) {
      differences[key] = { oldValue: obj1[key], newValue: obj2[key] };
    }
  });

  return differences;
};

const formatDifferences = (obj: any) => {
  const newValues: any = {};

  Object.keys(obj).forEach((key) => {
    newValues[key] = obj[key].newValue;
  });

  return newValues;
};

const getFlatWebpageObj = (obj: WebpageRecord | null) => {
  if (obj !== null) {
    return {
      eventId: obj.eventId,
      id: obj.id,
      jsonConfig: returnDefaultWebpageJsonConfig(obj.jsonConfig),
    };
  } else {
    return {};
  }
};

export const getFlatWidgetObj = (obj: WidgetRecord | null) => {
  if (obj !== null) {
    return {
      eventId: obj.eventId,
      id: obj.id,
      type: obj.type,
      language: obj.defaultLanguage,
      defaultLanguage: obj.defaultLanguage,
      title: obj.translations[obj.defaultLanguage].title,
      options: obj.translations[obj.defaultLanguage].options,
      widgetId: obj.widgetId,
      sortorder: obj.sortorder,
      jsonConfig: returnDefaultJsonConfig(obj.type, obj.config),
      content: obj.translations[obj.defaultLanguage].content || "",
      min: obj.min,
      max: obj.max,
      defaultfield: obj.defaultfield || null,
      required: obj.required || false,
      attendance: obj.attendance || false,
      adminfield: obj.adminfield || false,
    };
  } else {
    return {};
  }
};

const ContentSidePanel = ({
  config,
  setConfig,
}: {
  config: SidePanelConfig;
  setConfig: Function;
}) => {
  const { activeWidget, activeWebpage } = config;
  const [widgetInitialValues, setWidgetInitialValues] = useState<any>(
    getFlatWidgetObj(null)
  );
  const [webpageInitialValues, setWebpageInitialValues] = useState<any>(
    getFlatWebpageObj(null)
  );
  const revalidator = useRevalidator();

  useEffect(() => {
    if (activeWidget) {
      setWidgetInitialValues(getFlatWidgetObj(activeWidget));
    }
  }, [activeWidget]);

  useEffect(() => {
    if (activeWebpage) {
      setWebpageInitialValues(getFlatWebpageObj(activeWebpage));
    }
  }, [activeWebpage]);

  useEffect(() => {}, [webpageInitialValues]);

  const clearActiveField = () => {
    if (activeWidget) {
      setWidgetInitialValues(getFlatWidgetObj(null));
    }

    if (activeWebpage) {
      setWebpageInitialValues(getFlatWebpageObj(null));
    }

    setConfig((prev: SidePanelConfig) => ({
      ...prev,
      activeWidget: null,
      activeWebpage: null,
    }));
  };

  if (
    activeWidget &&
    activeWidget !== null &&
    Object.keys(widgetInitialValues).length > 0
  ) {
    return (
      <Formik
        initialValues={widgetInitialValues}
        enableReinitialize={true}
        onSubmit={(values) => {
          const differences = findDifferences(widgetInitialValues, values);
          const newValues = formatDifferences(differences);
          if (newValues.jsonConfig) {
            newValues.jsonConfig = JSON.stringify({
              ...widgetInitialValues.jsonConfig,
              ...newValues.jsonConfig,
            });
          }

          if (activeWidget !== null && Object.keys(newValues).length > 0) {
            fetch("/api/widgets/" + activeWidget.id, {
              method: "put",
              headers: {
                Accept: "application/json",
                Authorization: "Bearer " + sessionStorage.getItem("jwt"),
                "Content-Type": "application/json",
              },
              body: JSON.stringify(newValues),
            })
              .then((res) => res.json())
              .then((jsondata) => {
                if (jsondata.success) {
                  revalidator.revalidate();
                }
              });
          }
        }}
      >
        {({ values, resetForm, isValid }) => {
          return (
            <SidePanel sx={{ maxWidth: "300px" }} onClose={clearActiveField}>
              <Form
                noValidate
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {activeWidget !== null &&
                    typeof activeWidget !== "undefined" && (
                      <TypeOptions
                        widget={activeWidget}
                        jsonConfig={activeWidget.jsonConfig}
                      />
                    )}
                </Box>
                <Button variant="contained" type="submit">
                  {translations["save"]}
                </Button>
              </Form>
            </SidePanel>
          );
        }}
      </Formik>
    );
  }

  if (
    activeWebpage &&
    activeWebpage !== null &&
    Object.keys(webpageInitialValues).length > 0
  ) {
    return (
      <Formik
        initialValues={webpageInitialValues}
        enableReinitialize={true}
        onSubmit={(values) => {
          const differences = findDifferences(webpageInitialValues, values);
          const newValues = formatDifferences(differences);
          if (newValues.jsonConfig) {
            newValues.jsonConfig = JSON.stringify({
              ...webpageInitialValues.jsonConfig,
              ...newValues.jsonConfig,
            });
          }

          if (activeWebpage !== null && Object.keys(newValues).length > 0) {
            fetch("/api/webpages/" + activeWebpage.id, {
              method: "put",
              headers: {
                Accept: "application/json",
                Authorization: "Bearer " + sessionStorage.getItem("jwt"),
                "Content-Type": "application/json",
              },
              body: JSON.stringify(newValues),
            })
              .then((res) => res.json())
              .then((jsondata) => {
                if (jsondata.success) {
                  revalidator.revalidate();
                }
              });
          }
        }}
      >
        {({ values, resetForm, isValid }) => {
          return (
            <SidePanel sx={{ maxWidth: "300px" }} onClose={clearActiveField}>
              <Form
                noValidate
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {activeWebpage !== null &&
                    typeof activeWebpage !== "undefined" && (
                      <TypeOptions
                        webpage={activeWebpage}
                        jsonConfig={activeWebpage.jsonConfig}
                      />
                    )}
                </Box>
                <Button variant="contained" type="submit">
                  {translations["save"]}
                </Button>
              </Form>
            </SidePanel>
          );
        }}
      </Formik>
    );
  }

  return null;
};

export default ContentSidePanel;
